/**
 * BasePage - Master Layout Component
 *
 * THE archetype for ALL pages in the application.
 * Provides consistent structure, SEO management, Header, Footer, and background patterns.
 *
 * Every page should use BasePage instead of creating its own layout structure.
 *
 * @example
 * <BasePage title="Home" description="Find your perfect apartment">
 *   <YourPageContent />
 * </BasePage>
 */
import { Helmet } from "react-helmet-async";
import { Box, type BaseDivProps } from "@/shared/ui/base";
import { Header, HEADER_HEIGHT } from "./Header";
import type { HeaderProps } from "./Header";
import { Footer } from "./Footer";
import { cn } from "@/shared/lib/utils/cn";

export interface BasePageProps extends BaseDivProps {
  /** Page title (for SEO and browser tab) */
  title?: string;
  /** Page meta description (for SEO) */
  description?: string;
  /** Additional meta keywords */
  keywords?: string;
  /** Show header (default: true) */
  showHeader?: boolean;
  /** Header variant */
  headerVariant?: "solid" | "transparent" | "scroll-aware" | "fixed";
  /** Custom header props */
  headerProps?: Omit<HeaderProps, "className" | "data-testid">;
  /** Show footer (default: true) */
  showFooter?: boolean;
  /** Maximum width for main content */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  /** Background style */
  background?: "white" | "gray" | "gradient";
  /** Show animated blob background (for landing/marketing pages) */
  withAnimatedBlobs?: boolean;
  /** Padding for main content area */
  padding?: "none" | "sm" | "md" | "lg";
  /** Center content vertically (useful for auth pages) */
  centerContent?: boolean;
}

const maxWidthClasses = {
  sm: "max-w-3xl",
  md: "max-w-4xl",
  lg: "max-w-5xl",
  xl: "max-w-7xl",
  "2xl": "max-w-[1600px]",
  full: "max-w-full",
} as const;

const backgroundClasses = {
  white: "bg-white",
  gray: "bg-gray-50",
  gradient: "bg-gradient-to-b from-gray-50 to-white",
} as const;

const paddingClasses = {
  none: "",
  sm: "py-8",
  md: "py-12",
  lg: "py-16",
} as const;

/**
 * BasePage Component
 *
 * Handles:
 * - SEO (title, description, keywords via react-helmet-async)
 * - Header inclusion and configuration
 * - Footer inclusion
 * - Responsive max-width containers
 * - Background patterns
 * - Consistent spacing
 */
export function BasePage({
  title,
  description,
  keywords,
  showHeader = true,
  headerVariant = "solid",
  headerProps,
  showFooter = true,
  maxWidth = "xl",
  background = "white",
  withAnimatedBlobs = false,
  padding = "md",
  centerContent = false,
  className,
  children,
  ...props
}: BasePageProps) {
  // Build full page title
  const pageTitle = title
    ? `${title} | Apaato`
    : "Apaato - Housing for Foreigners in Japan";

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        {description && <meta name="description" content={description} />}
        {keywords && <meta name="keywords" content={keywords} />}
      </Helmet>

      {/* Page Container */}
      <Box
        className={cn(
          "min-h-screen",
          backgroundClasses[background],
          withAnimatedBlobs && "relative overflow-hidden",
          className,
        )}
        {...props}
      >
        {/* Animated Background Blobs */}
        {withAnimatedBlobs && (
          <Box className="absolute inset-0 overflow-hidden pointer-events-none">
            <Box className="absolute -top-20 -right-20 w-96 h-96 bg-red-200/30 rounded-3xl animate-blob-smooth blur-3xl" />
            <Box
              className="absolute -bottom-32 -left-32 w-80 h-80 bg-red-200/30 rounded-3xl animate-blob-smooth blur-3xl"
              style={{ animationDelay: "2s" }}
            />
            <Box
              className="absolute top-1/3 left-1/4 w-48 h-48 bg-red-300/25 rounded-full animate-blob-smooth blur-3xl"
              style={{ animationDelay: "4s" }}
            />
            <Box
              className="absolute top-20 -left-16 w-64 h-64 bg-red-200/25 rounded-full animate-blob-smooth blur-2xl"
              style={{ animationDelay: "1s" }}
            />
            <Box
              className="absolute bottom-20 right-10 w-56 h-56 bg-red-200/30 rounded-full animate-blob-smooth blur-3xl"
              style={{ animationDelay: "3s" }}
            />
          </Box>
        )}

        {/* Header */}
        {showHeader && (
          <Header
            position={
              headerVariant === "fixed"
                ? "fixed"
                : headerVariant === "scroll-aware"
                  ? "fixed"
                  : "relative"
            }
            variant={
              headerVariant === "scroll-aware"
                ? "scroll-aware"
                : headerVariant === "transparent"
                  ? "transparent"
                  : "solid"
            }
            showShadow={
              headerVariant === "scroll-aware" || headerVariant === "fixed"
            }
            {...headerProps}
          />
        )}

        {/* Main Content */}
        <Box
          as="main"
          className={cn(
            maxWidthClasses[maxWidth],
            "mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 relative z-10",
            paddingClasses[padding],
            // Add top padding if header is fixed/scroll-aware
            (headerVariant === "fixed" || headerVariant === "scroll-aware") &&
              `pt-24`,
            centerContent &&
              "flex items-center justify-center min-h-[calc(100vh-200px)]",
          )}
          style={
            headerVariant === "fixed" || headerVariant === "scroll-aware"
              ? { paddingTop: `${HEADER_HEIGHT + 24}px` }
              : undefined
          }
        >
          {children}
        </Box>

        {/* Footer */}
        {showFooter && <Footer maxWidth={maxWidth} />}
      </Box>
    </>
  );
}

BasePage.displayName = "BasePage";
